import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  Calendar,
  Clock,
  Tag,
  User,
  Phone,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Breadcrumbs from "../components/Breadcrumbs";
import SEO from "../components/SEO";
import CalculatorCallout from "../components/CalculatorCallout";
import AuthorBox from "../components/AuthorBox";
import NwflInboundLink from "../components/NwflInboundLink";
import { blogPosts } from "../data/blog";
import { businessInfo } from "../mock";
import { breadcrumbSchema } from "../utils/schemas";

// Per-category callout copy — keeps the calculator link contextual
// to whatever the homeowner just finished reading about.
const calloutForCategory = (category) => {
  switch (category) {
    case "Denied Claims":
      return {
        eyebrow: "Denial Isn't The End",
        headline:
          "See What Your Denied Claim Could Recover.",
        body: "You just read the playbook for overturning a denial. The next step is putting a real number on what you should be paid. Run our calculator — we'll show you a defensible range and reach out to discuss reopening.",
        ctaLabel: "Estimate My Claim",
      };
    case "Underpaid Claims":
      return {
        eyebrow: "The Check Wasn't Enough",
        headline: "Find Out The Real Number Before You Cash It.",
        body: "If your carrier underpaid you, the supplement playbook only works once you know what the claim is actually worth. Our calculator gives you that number in 60 seconds — using current Florida market rates.",
        ctaLabel: "Calculate My Shortfall",
      };
    case "Hurricane Claims":
      return {
        eyebrow: "Hurricane Damage Adds Up",
        headline: "Get The Full Storm Damage Number.",
        body: "Wind, water, roof, contents — hurricane claims have moving parts that homeowners miss. Walk through our calculator and we'll add it all up using current Florida market rates.",
        ctaLabel: "Run My Storm Estimate",
      };
    case "Education":
      return {
        eyebrow: "Stop Wondering, Start Estimating",
        headline: "Put A Number On It.",
        body: "You've got the knowledge — now apply it to your specific damage. Our calculator translates everything you just read into an actual dollar range for your home.",
        ctaLabel: "Try The Calculator",
      };
    default:
      return {
        eyebrow: "Ready For A Real Number?",
        headline: "Estimate Your Claim In 60 Seconds.",
        body: "The article gave you the framework. The calculator gives you the number. Walk through a few quick questions and we'll show you what your claim should be worth.",
        ctaLabel: "Get My Estimate",
      };
  }
};

const renderBlock = (block, i) => {
  if (block.type === "h2") {
    return (
      <h2
        key={i}
        className="text-white text-2xl sm:text-3xl font-black uppercase mt-10 mb-4 leading-tight"
        style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
      >
        {block.text}
      </h2>
    );
  }
  if (block.type === "p") {
    return (
      <p
        key={i}
        className="text-white/80 leading-relaxed mb-5 text-base sm:text-lg"
      >
        {block.text}
      </p>
    );
  }
  if (block.type === "linkedP") {
    return (
      <p
        key={i}
        className="text-white/80 leading-relaxed mb-5 text-base sm:text-lg"
      >
        {block.segments.map((seg, j) =>
          typeof seg === "string" ? (
            <React.Fragment key={j}>{seg}</React.Fragment>
          ) : (
            <Link
              key={j}
              to={seg.to}
              className="text-[#E11D26] hover:text-white underline underline-offset-2 font-semibold transition-colors"
            >
              {seg.text}
            </Link>
          )
        )}
      </p>
    );
  }
  if (block.type === "list") {
    return (
      <ul key={i} className="space-y-2 mb-6 ml-1">
        {block.items.map((item, j) => (
          <li
            key={j}
            className="flex items-start gap-3 text-white/80 leading-relaxed"
          >
            <span className="text-[#E11D26] font-bold mt-1 shrink-0">▸</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  }
  return null;
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) return <Navigate to="/blog" replace />;

  const related = blogPosts.filter((p) => p.slug !== slug).slice(0, 3);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.metaDescription,
    image: post.image,
    datePublished: post.date,
    dateModified: post.date,
    author: {
      "@type": "Person",
      name: post.author,
    },
    publisher: {
      "@type": "Organization",
      name: "Claim Beast Public Adjusters",
      logo: {
        "@type": "ImageObject",
        url: "https://claimbeast.com/assets/claim-beast-logo.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://claimbeast.com/blog/${post.slug}`,
    },
    articleSection: post.category,
  };

  const schemas = [
    articleSchema,
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Blog", path: "/blog" },
      { name: post.title, path: `/blog/${post.slug}` },
    ]),
  ];

  return (
    <div className="bg-black min-h-screen text-white">
      <SEO
        title={post.metaTitle}
        description={post.metaDescription}
        path={`/blog/${post.slug}`}
        keywords={post.keywords}
        image={post.image}
        schemas={schemas}
      />
      <Header />
      <Breadcrumbs
        items={[
          { name: "Blog", path: "/blog" },
          { name: post.title, path: `/blog/${post.slug}` },
        ]}
      />

      {/* Hero */}
      <section className="relative bg-black overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/85 to-black" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-14 md:py-20">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[#E11D26] hover:text-white text-xs font-bold tracking-[0.2em] uppercase transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            All Articles
          </Link>
          <div className="mt-5 flex flex-wrap items-center gap-3 text-xs text-white/65">
            <span className="flex items-center gap-1.5">
              <Tag className="h-3.5 w-3.5 text-[#E11D26]" />
              {post.category}
            </span>
            <span>·</span>
            <span className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              {post.readTime}
            </span>
            <span>·</span>
            <span className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              {new Date(post.date).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </span>
            <span>·</span>
            <span className="flex items-center gap-1.5">
              <User className="h-3.5 w-3.5" />
              {post.author}
            </span>
          </div>
          <h1
            className="mt-4 text-white text-3xl sm:text-5xl md:text-6xl font-black uppercase leading-[0.95]"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            {post.title}
          </h1>
          <p className="mt-5 text-white/80 text-base sm:text-lg leading-relaxed">
            {post.excerpt}
          </p>
        </div>
      </section>

      {/* Article content */}
      <article className="bg-[#0A0A0A] py-14 md:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          {post.content.map((block, i) => {
            // Inject a compact, category-specific calculator callout
            // roughly a third of the way through every post.
            const injectAt = Math.max(2, Math.floor(post.content.length / 3));
            const callout =
              i === injectAt ? (
                <CalculatorCallout
                  key={`callout-${i}`}
                  compact
                  ctaLabel={calloutForCategory(post.category).ctaLabel}
                  body={calloutForCategory(post.category).body}
                />
              ) : null;
            return (
              <React.Fragment key={i}>
                {callout}
                {renderBlock(block, i)}
              </React.Fragment>
            );
          })}

          <AuthorBox author={post.author} />

          {/* Inline CTA */}
          <div className="mt-12 bg-gradient-to-br from-[#1a0709] to-[#0F0F0F] border-l-4 border-[#E11D26] p-6 sm:p-8">
            <div className="text-[#E11D26] text-xs font-bold tracking-[0.25em] uppercase">
              Need Help With Your Claim?
            </div>
            <h3
              className="mt-3 text-white text-2xl sm:text-3xl font-black uppercase leading-tight"
              style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
            >
              Free Tampa Bay Claim Review — No Win, No Fee.
            </h3>
            <p className="mt-3 text-white/75 leading-relaxed">
              Call Claim Beast at {businessInfo.phone} or fill out our online
              form for a free review of your insurance claim.
            </p>
            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <Link
                to="/#contact"
                className="group inline-flex items-center gap-3 bg-[#E11D26] hover:bg-[#c11820] text-white font-bold uppercase tracking-wider px-6 py-3 transition-colors"
              >
                <span className="text-sm">Free Claim Review</span>
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href={`tel:${businessInfo.phoneRaw}`}
                className="inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-white/90 font-bold uppercase tracking-wider px-6 py-3 transition-colors text-sm"
              >
                <Phone className="h-4 w-4" />
                {businessInfo.phone}
              </a>
            </div>
          </div>
        </div>
      </article>

      {/^Hurricane/.test(post.category || "") && <NwflInboundLink topic="hurricane" />}

      {/* Related */}
      <section className="bg-black py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2
            className="text-3xl font-black text-white uppercase mb-7"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            More From The <span className="text-[#E11D26]">Blog</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {related.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="group bg-[#0F0F0F] border border-white/10 hover:border-[#E11D26]/50 transition-colors overflow-hidden"
              >
                <img
                  src={p.image}
                  alt={p.title}
                  className="w-full aspect-[16/10] object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="p-5">
                  <div className="text-[#E11D26] text-[11px] font-bold tracking-[0.2em] uppercase">
                    {p.category}
                  </div>
                  <h3
                    className="mt-2 text-white text-lg font-black uppercase leading-tight group-hover:text-[#E11D26] transition-colors"
                    style={{
                      fontFamily: "'Anton', 'Oswald', sans-serif",
                    }}
                  >
                    {p.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPostPage;
